import React, { useEffect, useState, useRef, useMemo } from "react";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import JobApplicationDetailsHeader from "./JobApplicationDetailsHeader";
import { useLoader } from "../../../../Context/LoaderContext";
import { JobApplication } from "../../../../Entities/JobApplication";
import { openNotification } from "../../../../Components/Notification";
import jobDescriptionService from "../../../../Service/job.description.service";
import jobApplicationService from "../../../../Service/job.application.service";
import interviewService from "../../../../Service/interview.service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MatchResult } from "../../../../Entities/MatchResult";
import { UserProfile } from "../../../../Entities/UserProfile";
import { Tabs, Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import JobApplicationSummary from "../JobApplicationSummary/JobApplicationSummary";
import JobApplicationProfileMatchResult from "../JobApplicationProfileMatchResult/JobApplicationProfileMatchResult";
import JobApplicationInterviewRounds from "../JobApplicationInterviewRounds/JobApplicationInterviewRounds";
import JobApplicationScheduledInterview from "../JobApplicationScheduledInterview/JobApplicationScheduledInterview";
import { Session } from "../../../../Entities/Session";
import profileService from "../../../../Service/profile.service";
import userService from "../../../../Service/user.service";
import { Interview } from "../../../../Entities/Interview";
import ResumePreviewDrawer from "./ResumePreviewDrawer";
import { useApplicationStageChange } from "../../hooks/useApplicationStageChange";
import OnboardingMatchModal from "./OnboardingMatchModal";
import "./JobApplicationDetails.scss";

interface JobApplicationDetailsProps {
  applicationId: string;
  jobPostId: string;
  selectedApplication: JobApplication | null;
  setSelectedApplication: React.Dispatch<
    React.SetStateAction<JobApplication | null>
  >;
}

const JobApplicationDetails: React.FC<JobApplicationDetailsProps> = ({
  applicationId,
  jobPostId,
  selectedApplication,
  setSelectedApplication,
}) => {
  const { setLoading, setLoadingMessage } = useLoader();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [activeTabKey, setActiveTabKey] = useState<string>(
    searchParams.get("tab") || "summary",
  );
  const [jobPost, setJobPost] = useState<JobDescriptionEntity | null>(null);
  const [interviewRounds, setInterviewRounds] = useState<InterviewRound[]>([]);
  const [profileMatchResult, setProfileMatchResult] =
    useState<MatchResult | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [interviewSessions, setInterviewSessions] = useState<Session[]>([]);
  const [interviewSchedule, setInterviewSchedule] = useState<Interview | null>(
    null,
  );
  const [isResumeViewing, setIsResumeViewing] = useState<{
    isResumeViewing: boolean;
    resumeUrl: string | null;
  }>({
    isResumeViewing: false,
    resumeUrl: null,
  });
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const isInitialDataLoaded = useRef(false);
  const isProgrammaticTabChange = useRef(false);

  // Match result modal — shown once when arriving from onboarding (showMatchResult=1 URL param)
  const [showOnboardingMatchModal, setShowOnboardingMatchModal] = useState(false);
  const matchModalShownRef = useRef(false);

  const {
    handleMoveToAIAssessmentRound,
    handleRestartAIAssessmentRound,
    handleMarkAsShortlisted,
    handleMarkAsRejected,
    handleMarkAsHired,
    handleAllowInterviewSchedule,
    handleSendReminderNotification
  } = useApplicationStageChange({
    jobApplication: selectedApplication,
    setJobApplication: setSelectedApplication,
    jobPost: jobPost,
    interviewSessions: interviewSessions,
    setInterviewSessions: setInterviewSessions,
  });

  useEffect(() => {
    const init = async () => {
      if (!applicationId) {
        console.error("JobApplicationDetails -> init: No application selected");
        return;
      }

      if (!jobPostId) {
        console.error("JobApplicationDetails -> init: No job post id");
        return;
      }

      setLoading(true);
      setLoadingMessage("Loading job application details...");

      try {
        const [jobDescription, jobApplication] = await Promise.all([
          jobDescriptionService.getJobDescriptionById(jobPostId),
          jobApplicationService.getJobApplicationById(applicationId),
        ]);

        if (!jobDescription) {
          console.error(
            "JobApplicationDetails -> init: No job description found",
          );
           navigate("/", { replace: true });
          return;
        }

        if (!jobApplication) {
          console.error(
            "JobApplicationDetails -> init: No job application found",
          );
          navigate("/", { replace: true });
          return;
        }

        setJobPost(jobDescription);
        setSelectedApplication(jobApplication);

        if (
          jobDescription.interviewRounds &&
          jobDescription.interviewRounds.length > 0
        ) {
          setLoadingMessage("Loading assessments...");
          const [interviewSessionData, interviewRoundsData] = await Promise.all(
            [
              interviewService.getSessionByJobApplicationId(applicationId),
              interviewService.getAllInterviewRoundsSpecificToJob(jobPostId),
            ],
          );
          const sortedInterviewSessionData = interviewSessionData.sort(
            (a: Session, b: Session) =>
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          );
          setInterviewSessions(sortedInterviewSessionData);
          setInterviewRounds(interviewRoundsData);
          setSelectedSession(sortedInterviewSessionData[0] || null);
        }

        if (jobApplication?.profileId && jobApplication?.profileMatchStatus) {
          setLoadingMessage("Loading profile match result...");
          const [profileMatchResultData, userProfileData] = await Promise.all([
            profileService.getMatchResultByJobApplicationId(applicationId),
            userService.getUserProfileByProfileId(jobApplication.profileId),
          ]);
          setProfileMatchResult(profileMatchResultData[0] || null);
          setUserProfile(userProfileData);
        }

        if (
          jobDescription.onlineInterview &&
          jobApplication?.interviewSchedule
        ) {
          setLoadingMessage("Loading interview schedule...");
          const interviewScheduleData =
            await interviewService.getInterviewScheduleByJobApplicationId(
              applicationId,
            );
          setInterviewSchedule(interviewScheduleData);
        } else {
          if (activeTabKey === "scheduled-interview") {
            setActiveTabKey("summary");
          }
        }

        // Mark initial data load as complete
        isInitialDataLoaded.current = true;
      } catch (error: any) {
        console.error("Error initializing job application details:", error);
        openNotification(
          "error",
          "Failed to fetch job application details",
          "",
        );
        // Mark as loaded even on error to prevent infinite waiting
        isInitialDataLoaded.current = true;
      } finally {
        setLoading(false);
        setLoadingMessage(null);
      }
    };

    init();
  }, [jobPostId, applicationId]);

  // Reset initial data loaded flag when application or job post changes
  useEffect(() => {
    isInitialDataLoaded.current = false;
    matchModalShownRef.current = false;
  }, [applicationId, jobPostId]);

  // Show onboarding match result modal once profileMatchResult is available,
  // but only when arriving from onboarding with showMatchResult=1 URL param.
  useEffect(() => {
    if (matchModalShownRef.current) return;
    const shouldShow = searchParams.get("showMatchResult") === "1";
    if (!shouldShow || !profileMatchResult) return;
    matchModalShownRef.current = true;
    setShowOnboardingMatchModal(true);
  }, [searchParams, profileMatchResult]);

  // Refetch profile match result when application status changes (hook handles interview sessions)
  // This ensures UI updates reactively without page refresh
  const previousStatusRef = useRef<string | undefined>(
    selectedApplication?.status,
  );
  const previousApplicationIdRef = useRef<string | undefined>(applicationId);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const refetchProfileMatch = async () => {
      if (!selectedApplication || !applicationId || !jobPost) {
        return;
      }

      // Skip if this is the initial mount
      if (isInitialMount.current) {
        isInitialMount.current = false;
        previousStatusRef.current = selectedApplication?.status;
        previousApplicationIdRef.current = applicationId;
        return;
      }

      // Skip if application ID changed (handled by init useEffect)
      if (previousApplicationIdRef.current !== applicationId) {
        previousApplicationIdRef.current = applicationId;
        previousStatusRef.current = selectedApplication?.status;
        return;
      }

      // Only refetch if status actually changed
      if (previousStatusRef.current === selectedApplication?.status) {
        return;
      }

      previousStatusRef.current = selectedApplication?.status;

      // Only refetch profile match result when status changes and profile exists
      // Interview sessions are handled by the hook to avoid duplicate API calls
      try {
        if (
          selectedApplication?.profileId &&
          selectedApplication?.profileMatchStatus
        ) {
          const profileMatchResultData =
            await profileService.getMatchResultByJobApplicationId(
              applicationId,
            );
          setProfileMatchResult(profileMatchResultData[0] || null);
        }
      } catch (error: any) {
        console.error("Error refetching profile match result:", error);
      }
    };

    refetchProfileMatch();
  }, [
    selectedApplication?.status,
    selectedApplication?._id,
    applicationId,
    jobPost?._id,
  ]);

  // Update selectedSession when interviewSessions changes
  useEffect(() => {
    if (interviewSessions.length > 0) {
      // If no session is currently selected, or the selected session is not in the new list, select the first one
      const currentSelectedId = selectedSession?._id;
      const isSelectedSessionInList = currentSelectedId
        ? interviewSessions.some((s) => s._id === currentSelectedId)
        : false;

      if (!currentSelectedId || !isSelectedSessionInList) {
        setSelectedSession(interviewSessions[0] || null);
      }
    } else {
      // If no sessions, clear selected session
      if (selectedSession) {
        setSelectedSession(null);
      }
    }
  }, [interviewSessions, selectedSession?._id]);

  // Sync activeTabKey with URL params (for browser back/forward navigation)
  // Skip sync if data isn't loaded yet to avoid conflicts
  useEffect(() => {
    if (!isInitialDataLoaded.current) {
      return;
    }
    const tabFromUrl = searchParams.get("tab") || "summary";
    // Only sync if URL changed (browser navigation), not if we just updated it programmatically
    // Check if URL actually changed by comparing with current activeTabKey
    if (tabFromUrl !== activeTabKey) {
      setActiveTabKey(tabFromUrl);
    }
  }, [searchParams]);

  const handleTabChange: React.Dispatch<React.SetStateAction<string>> = (
    key,
  ) => {
    // Mark as programmatic change to bypass ref checks
    isProgrammaticTabChange.current = true;

    // Handle both string and function forms of SetStateAction
    const newKey = typeof key === "function" ? key(activeTabKey) : key;

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("tab", newKey);
      return newParams;
    });
    setActiveTabKey(newKey);
    // Only reset selected session if switching away from interview-rounds tab
    // When switching to interview-rounds, preserve the current session (if any)
    // This allows handleViewDetails to set a specific session before switching tabs
    if (newKey !== "assessments") {
      setSelectedSession(interviewSessions[0] || null);
    }
    // Reset flag after a short delay
    setTimeout(() => {
      isProgrammaticTabChange.current = false;
    }, 100);
  };

  // Filter tabs based on data availability
  const availableTabs = useMemo(() => {
    const tabs = [];

    // Summary tab is always available
    tabs.push({
      key: "summary",
      label: "Summary",
    });

    // Profile Match tab - only show if profileMatchResult and userProfile are available
    if (profileMatchResult && userProfile) {
      tabs.push({
        key: "profile-match",
        label: "Profile Match ",
      });
    }

    // Assessments tab - only show if interview rounds and sessions are available
    const hasInterviewRounds =
      jobPost?.interviewRounds &&
      jobPost.interviewRounds.length > 0 &&
      interviewRounds.length > 0 &&
      interviewSessions.length > 0;
    if (hasInterviewRounds) {
      const isPartiallyQualified =
        selectedApplication?.questionnaireStatus === "partially_qualified";
      tabs.push({
        key: "assessments",
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            Assessments
            {isPartiallyQualified && (
              <Tooltip title="Action Required">
                <WarningOutlined style={{ color: "red", fontSize: "14px" }} />
              </Tooltip>
            )}
          </span>
        ),
      });
    }

    // Scheduled Interview tab - only show if online interview is enabled and schedule exists
    if (jobPost && jobPost?.onlineInterview && interviewSchedule) {
      tabs.push({
        key: "scheduled-interview",
        label: "Scheduled Interview",
      });
    }

    return tabs;
  }, [
    profileMatchResult,
    userProfile,
    jobPost,
    interviewRounds,
    interviewSchedule,
    selectedApplication?.questionnaireStatus,
  ]);

  // Handle case where active tab becomes unavailable
  // Only check after initial data load to preserve tab from URL on page refresh
  // Skip check if tab change is programmatic (e.g., from Action Required button)
  useEffect(() => {
    // Don't check tab availability until initial data is loaded
    if (!isInitialDataLoaded.current) {
      return;
    }

    // Skip check if this is a programmatic tab change (e.g., Action Required button)
    if (isProgrammaticTabChange.current) {
      return;
    }

    const availableTabKeys = availableTabs.map((tab) => tab.key);
    if (!availableTabKeys.includes(activeTabKey)) {
      // Switch to the first available tab (should always be "summary")
      const firstAvailableTab = availableTabKeys[0] || "summary";
      setActiveTabKey(firstAvailableTab);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("tab", firstAvailableTab);
        return newParams;
      });
    }
  }, [activeTabKey, availableTabs, setSearchParams]);

  const handleDismissOnboardingMatchModal = () => {
    setShowOnboardingMatchModal(false);
    // Clear the cta_type from sessionStorage now that the modal has been seen
    sessionStorage.removeItem("screening_cta_type");
    // Remove showMatchResult from URL without triggering a reload
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("showMatchResult");
      return next;
    }, { replace: true });
  };

  if (!selectedApplication || !jobPost) {
    return null;
  }

  const getActiveTabChildren = () => {
    switch (activeTabKey) {
      case "summary":
        return (
          <JobApplicationSummary
            jobApplication={selectedApplication}
            profileMatchResult={profileMatchResult}
            jobPost={jobPost}
            profile={userProfile}
            interviewRounds={interviewRounds}
            interviewSessions={interviewSessions}
            setActiveTabKey={handleTabChange}
            setIsResumeViewing={setIsResumeViewing}
            setSelectedSession={setSelectedSession}
            handleMoveToAIAssessmentRound={handleMoveToAIAssessmentRound}
            handleRestartAIAssessmentRound={handleRestartAIAssessmentRound}
            handleMarkAsShortlisted={handleMarkAsShortlisted}
            handleMarkAsRejected={handleMarkAsRejected}
            handleMarkAsHired={handleMarkAsHired}
            handleAllowInterviewSchedule={handleAllowInterviewSchedule}
            handleSendReminderNotification={handleSendReminderNotification}
            setProfileMatchResult={setProfileMatchResult}
            setSelectedApplication={setSelectedApplication}
            setUserProfile={setUserProfile}
          />
        );
      case "profile-match":
        return (
          <JobApplicationProfileMatchResult
            profileMatchResult={profileMatchResult}
            setIsResumeViewing={setIsResumeViewing}
            userProfile={userProfile}
            jobPost={jobPost}
          />
        );
      case "assessments":
        return (
          <JobApplicationInterviewRounds
            interviewRounds={interviewRounds}
            interviewSessions={interviewSessions}
            jobPost={jobPost}
            selectedSession={selectedSession}
            setSelectedSession={setSelectedSession}
            jobApplication={selectedApplication}
            setJobApplication={setSelectedApplication}
            setInterviewSessions={setInterviewSessions}
            setActiveTabKey={handleTabChange}
          />
        );
      case "scheduled-interview":
        return (
          <JobApplicationScheduledInterview
            scheduledInterview={interviewSchedule}
            jobApplication={selectedApplication}
            jobPost={jobPost}
          />
        );
      default:
        return (
          <JobApplicationSummary
            jobApplication={selectedApplication}
            profileMatchResult={profileMatchResult}
            jobPost={jobPost}
            profile={userProfile}
            interviewRounds={interviewRounds}
            interviewSessions={interviewSessions}
            setActiveTabKey={handleTabChange}
            setIsResumeViewing={setIsResumeViewing}
            setSelectedSession={setSelectedSession}
            handleMoveToAIAssessmentRound={handleMoveToAIAssessmentRound}
            handleRestartAIAssessmentRound={handleRestartAIAssessmentRound}
            handleMarkAsShortlisted={handleMarkAsShortlisted}
            handleMarkAsRejected={handleMarkAsRejected}
            handleMarkAsHired={handleMarkAsHired}
            handleAllowInterviewSchedule={handleAllowInterviewSchedule}
            handleSendReminderNotification={handleSendReminderNotification}
            setProfileMatchResult={setProfileMatchResult}
            setSelectedApplication={setSelectedApplication}
            setUserProfile={setUserProfile}
          />
        );
    }
  };

  return (
    <section className="job-application-details-container">
      <JobApplicationDetailsHeader
        jobPostId={jobPostId}
        title={jobPost?.title || ""}
      />
      <Tabs
        style={{ margin: "0px", width: "100%" }}
        activeKey={activeTabKey}
        items={availableTabs}
        onChange={handleTabChange}
        size="small"
        // tabBarExtraContent={
        //   <ExportPDFButton
        //     jobApplication={selectedApplication}
        //     jobPost={jobPost}
        //     profileMatchResult={profileMatchResult}
        //     profile={userProfile}
        //     interviewSessions={interviewSessions}
        //     interviewRounds={interviewRounds}
        //     scheduleData={interviewSchedule}
        //   />
        // }
      />
      <div className="job-application-details-content-wrapper">
        {getActiveTabChildren()}
      </div>
      <ResumePreviewDrawer
        setIsResumeViewing={setIsResumeViewing}
        isResumeViewing={isResumeViewing}
        userProfile={userProfile}
      />

      {/* Onboarding match result modal — shown once when arriving from /user-onboarding */}
      {profileMatchResult && (
        <OnboardingMatchModal
          profileMatchResult={profileMatchResult}
          open={showOnboardingMatchModal}
          onDismiss={handleDismissOnboardingMatchModal}
          jobPostId={jobPostId}
          onMoveToAssessment={handleMoveToAIAssessmentRound}
          onSendRoundEmail={() => handleSendReminderNotification("round")}
        />
      )}
    </section>
  );
};

export default JobApplicationDetails;
